import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script'
import * as gtag from '@/lib/gtag';
import * as fbq from '@/lib/fbq';
import { trackEvent } from '@/lib/intixAnalytics';
import Bugsnag from '@/lib/bugsnag';
import '@stripe/stripe-js';

/* Global CSS Styles */
import '@/styles/bootstrap.css';
import '@/styles/style.css';
import '@/styles/react-datetime.css';

// Guards against an infinite reload loop: we only force one reload per failed
// navigation. Cleared on the next successful route change (see handleRouteChange)
// so a later stale-asset event in the same session can still recover.
const CHUNK_RELOAD_KEY = 'intix_chunk_reload';

// True when a navigation or dynamic import fails because a hashed _next/static
// chunk no longer exists on the server. This typically happens when a new deploy
// replaces the static assets while the tab was already open, so the cached page
// references chunk filenames that now 404.
function isChunkLoadError( error ){
	if( !error ){ return false; }
	const name = error.name || '';
	const message = error.message || '';
	return name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test( message ) || /Failed to fetch dynamically imported module/i.test( message );
}

// Force a single full-document reload so the browser fetches the current build's
// manifest and chunk filenames, instead of leaving the user on a half-rendered or
// unstyled page. Guarded against reload loops via sessionStorage.
function recoverFromChunkError(){
	try {
		if( window.sessionStorage.getItem( CHUNK_RELOAD_KEY ) ){ return; }
		window.sessionStorage.setItem( CHUNK_RELOAD_KEY, '1' );
	} catch( e ){ /* sessionStorage unavailable (private mode / disabled): reload once anyway */ }
	window.location.reload();
}

export default function App({ Component, pageProps }){
	const router = useRouter();
	const ErrorBoundary = Bugsnag.getPlugin('react');
	const org_id = pageProps.event?.org_id || pageProps.org?.org_id || undefined;
	const event_id = pageProps.event?.event_id || undefined;

	// Log against the page being viewed, so a visit that moves on to /account, /manage or another event isn't counted against the first event
	useEffect(() => {
		if( router.isReady && org_id ){ trackEvent('page_view', org_id, event_id, { page_path: router.asPath }); }
	}, [router.isReady, router.asPath.split(/[?#]/)[0]]);

	useEffect(() => {
		fbq.pageview();
		gtag.pageview(router.asPath);

		const handleRouteChange = (url) => {
			try { window.sessionStorage.removeItem( CHUNK_RELOAD_KEY ); } catch( e ){}
			gtag.pageview(url);
			fbq.pageview();
		}

		// Recover from stale-asset failures (missing _next/static chunks after a deploy)
		// by reloading once, rather than leaving the user stuck on an unstyled page.
		// Detection is strict so unrelated runtime errors never trigger a reload.
		const handleRouteError = ( error ) => { if( isChunkLoadError( error ) ){ recoverFromChunkError(); } }
		const handleWindowError = ( event ) => { if( isChunkLoadError( event.error ) ){ recoverFromChunkError(); } }
		const handleRejection = ( event ) => { if( isChunkLoadError( event.reason ) ){ recoverFromChunkError(); } }

		//When the component is mounted, subscribe to router changes and log those page views
		router.events.on('routeChangeComplete', handleRouteChange)
		router.events.on('routeChangeError', handleRouteError)
		window.addEventListener('error', handleWindowError)
		window.addEventListener('unhandledrejection', handleRejection)

		// If the component is unmounted, unsubscribe from the event with the `off` method
		return () => {
			router.events.off('routeChangeComplete', handleRouteChange)
			router.events.off('routeChangeError', handleRouteError)
			window.removeEventListener('error', handleWindowError)
			window.removeEventListener('unhandledrejection', handleRejection)
		}
	}, [router.events]);

	return ( <ErrorBoundary FallbackComponent={Error}><Component {...pageProps} /></ErrorBoundary> )
}
